/**
 * @module ToastBridge
 * @package @geenius/adapters/react-native
 * @description Native toast-style notification bridge for adapter telemetry.
 */

import type { JSX } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { Pressable, StyleSheet, Text, View } from "../lib/native";
import { semanticColors } from "./TokenBridge";

/**
 * Props for the native toast bridge.
 *
 * @property kind Semantic tone used for the toast colors.
 * @property message Required toast body text.
 * @property onDismiss Optional callback that renders a dismiss action.
 * @property style Optional native style merged onto the toast container.
 * @property testID Optional deterministic selector for tests and Storybook.
 * @property title Optional title shown before the message.
 * @property visible Controls whether the toast renders.
 */
export interface ToastBridgeProps {
  kind?: "error" | "info" | "success" | "warning";
  message: string;
  onDismiss?: () => void;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  title?: string;
  visible?: boolean;
}

const kindStyles: Record<
  NonNullable<ToastBridgeProps["kind"]>,
  { backgroundColor: string; borderColor: string; color: string }
> = {
  error: {
    backgroundColor: semanticColors.status.error.bg,
    borderColor: semanticColors.status.error.border,
    color: semanticColors.status.error.text,
  },
  info: {
    backgroundColor: semanticColors.status.initializing.bg,
    borderColor: semanticColors.status.initializing.border,
    color: semanticColors.status.initializing.text,
  },
  success: {
    backgroundColor: semanticColors.status.connected.bg,
    borderColor: semanticColors.status.connected.border,
    color: semanticColors.status.connected.text,
  },
  warning: {
    backgroundColor: semanticColors.status.warning.bg,
    borderColor: semanticColors.status.warning.border,
    color: semanticColors.status.warning.text,
  },
};

/**
 * Renders a native toast-style adapter notification.
 *
 * @param props Toast bridge props including message, tone, and dismiss callback.
 * @returns A native toast surface, or `null` when `visible` is false.
 * @example
 * ```tsx
 * <ToastBridge kind="error" title="Adapter offline" message="Retry later" />
 * ```
 */
export function ToastBridge({
  kind = "info",
  message,
  onDismiss,
  style,
  testID,
  title,
  visible = true,
}: ToastBridgeProps): JSX.Element | null {
  if (!visible) {
    return null;
  }

  const colors = kindStyles[kind];

  return (
    <View
      accessibilityLabel={title ? `${title}: ${message}` : message}
      accessibilityRole="summary"
      style={[
        styles.container,
        {
          backgroundColor: colors.backgroundColor,
          borderColor: colors.borderColor,
        },
        style,
      ]}
      testID={testID ?? "adapter-toast-bridge"}
    >
      <View style={styles.body}>
        {title && (
          <Text style={[styles.title, { color: colors.color }]}>{title}</Text>
        )}
        <Text style={[styles.message, { color: colors.color }]}>{message}</Text>
      </View>
      {onDismiss && (
        <Pressable
          accessibilityLabel="Dismiss adapter toast"
          accessibilityRole="button"
          onPress={onDismiss}
          style={styles.dismiss}
          testID="adapter-toast-dismiss"
        >
          <Text style={[styles.dismissText, { color: colors.color }]}>
            Close
          </Text>
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  body: {
    flex: 1,
  },
  container: {
    alignItems: "center",
    borderRadius: 10,
    borderWidth: 1,
    flexDirection: "row",
    gap: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  dismiss: {
    paddingHorizontal: 6,
    paddingVertical: 4,
  },
  dismissText: {
    fontSize: 12,
    fontWeight: "800",
  },
  message: {
    fontSize: 13,
    lineHeight: 18,
  },
  title: {
    fontSize: 13,
    fontWeight: "900",
    marginBottom: 2,
  },
});
